<div id="{{ uuid }}" class="relative flex flex-col gap-1 bg-white">
    <!-- Search filter -->
    @if (enabledFilter) {
        <div class="sticky top-0 z-10 border-b border-slate-100 bg-white p-3">
            <div class="relative">
                <i
                    class="fa-light fa-search absolute top-1/2 -translate-y-1/2 text-slate-400 ltr:left-3 rtl:right-3"
                ></i>
                <input
                    class="focus:ring-primary-500/10 w-full rounded-lg border border-slate-200 bg-white py-2 text-sm text-slate-800 placeholder-slate-400 transition-all focus:border-primary-500 focus:outline-none focus:ring-4 ltr:pl-9 ltr:pr-3 rtl:pl-3 rtl:pr-9"
                    placeholder="{{ 'translate_search' | translate }}"
                    type="text"
                    #search
                    (keyup)="filterItems(search.value)"
                />
            </div>
        </div>
    }

    <nav class="p-3">
        <ng-container
            [ngTemplateOutlet]="itemsListTemplate"
            [ngTemplateOutletContext]="{
                items: filteredItems,
            }"
        ></ng-container>
    </nav>
</div>

<!-- The main items list template -->
<ng-template #itemsListTemplate let-items="items">
    @for (item of items; track item) {
        @if (
            !item.isHidden &&
            (!item.isShownOnlyInSafeMode || safeModeService.isEnabled) &&
            hasAnyFeature(item.featureIds)
        ) {
            <ng-container
                [ngTemplateOutlet]="
                    item.type === 'item'
                        ? itemTemplate
                        : item.type === 'expandable'
                          ? expandableItemTemplate
                          : sectionItemTemplate
                "
                [ngTemplateOutletContext]="{ item: item }"
            ></ng-container>
        }
    }
</ng-template>

<!-- Expandable item template -->
<ng-template #expandableItemTemplate let-item="item">
    <div *appHasAnyPermissionId="item.permissionIdList" class="mb-1">
        <ng-container
            [ngTemplateOutlet]="linkItemTemplate"
            [ngTemplateOutletContext]="{ item: item }"
        ></ng-container>
        <div
            class="grid grid-cols-1 overflow-hidden transition-[grid-template-rows,opacity] duration-150 ease-[cubic-bezier(0.42,0,0.58,1)]"
            [ngClass]="{
                'grid-rows-[1fr] opacity-100': item.isOpen,
                'grid-rows-[0fr] opacity-0': !item.isOpen,
            }"
        >
            <div class="min-h-0">
                <div
                    class="mt-1 flex flex-col gap-0.5 rounded-lg bg-slate-50 p-2"
                >
                    <ng-container
                        [ngTemplateOutlet]="itemsListTemplate"
                        [ngTemplateOutletContext]="{
                            items: item.children,
                        }"
                    ></ng-container>
                </div>
            </div>
        </div>
    </div>
</ng-template>

<!-- Link item template -->
<ng-template #itemTemplate let-item="item">
    <div *appHasAnyPermissionId="item.permissionIdList" class="mb-0.5">
        <ng-container
            [ngTemplateOutlet]="linkItemTemplate"
            [ngTemplateOutletContext]="{ item: item }"
        ></ng-container>
    </div>
</ng-template>

<!-- Section item template -->
<ng-template #sectionItemTemplate let-item="item">
    @if (!isSidebarCollapsed && hasFeatureVisibleChildren(item)) {
        <div
            *appHasAnyPermissionId="item.permissionIdList"
            class="mb-2 mt-4 border-t border-slate-100 pt-4 first:mt-0 first:border-0 first:pt-0"
        >
            <p class="mb-2 truncate px-3 text-xs font-semibold text-slate-400">
                {{ item.title }}
            </p>
        </div>
    }
    <ng-container
        [ngTemplateOutlet]="itemsListTemplate"
        [ngTemplateOutletContext]="{
            items: item.children,
        }"
    ></ng-container>
</ng-template>

<!-- Link item template -->
<ng-template #linkItemTemplate let-item="item">
    @if (item.type === 'expandable') {
        <a
            href="javascript:void(0)"
            (click)="toggleExpandableItem(item)"
            [appTooltip]="item.title"
            class="relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium no-underline transition-colors hover:bg-slate-50 hover:no-underline"
            [ngClass]="{
                'justify-center': isSidebarCollapsed,
                'justify-between': !isSidebarCollapsed,
                'bg-primary-50 text-primary-700': item.isOpen || item.isActive,
                'text-slate-600': !(item.isOpen || item.isActive),
            }"
        >
            <!-- Active indicator line -->
            @if (item.isOpen || item.isActive) {
                <div
                    class="absolute bottom-1.5 top-1.5 w-0.5 rounded-full bg-primary-600 ltr:left-0 rtl:right-0"
                ></div>
            }
            <ng-container [ngTemplateOutlet]="linkContent"></ng-container>
        </a>
    } @else {
        <a
            [routerLink]="$any(item.type === 'item' ? item.link : null)"
            [appTooltip]="item.title"
            class="nav-item-link relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium no-underline transition-colors hover:bg-slate-50 hover:no-underline"
            [ngClass]="{
                'justify-center': isSidebarCollapsed,
                'justify-between': !isSidebarCollapsed,
                'bg-primary-50 text-primary-700': item.isOpen || item.isActive,
                'text-slate-600': !(item.isOpen || item.isActive),
            }"
        >
            <!-- Active indicator line -->
            @if (item.isOpen || item.isActive) {
                <div
                    class="absolute bottom-1.5 top-1.5 w-0.5 rounded-full bg-primary-600 ltr:left-0 rtl:right-0"
                ></div>
            }
            <ng-container [ngTemplateOutlet]="linkContent"></ng-container>
        </a>
    }

    <ng-template #linkContent>
        <div class="relative flex min-w-0 items-center gap-3">
            <em
                [ngClass]="item.icon || 'fa-light fa-circle-small'"
                class="w-5 shrink-0 text-center"
                [class.text-primary-600]="item.isOpen || item.isActive"
                [class.text-slate-400]="!(item.isOpen || item.isActive)"
            ></em>
            <!-- Collapsed badge: small dot indicator on icon -->
            @if (isSidebarCollapsed && item.badgeCount) {
                <span
                    class="absolute -top-1 end-0 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white"
                ></span>
            }
            @if (!isSidebarCollapsed) {
                <span class="truncate">
                    {{ item.title }}
                </span>
            }
        </div>
        @if (!isSidebarCollapsed) {
            <div class="flex items-center gap-2 empty:hidden">
                <!-- Badge count -->
                @if (item.badgeCount) {
                    <span
                        class="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700"
                    >
                        {{ item.badgeCount }}
                    </span>
                }
                <!-- Expand arrow -->
                @if (item.children?.length) {
                    <em
                        class="fa-solid fa-angle-down text-xs text-slate-400 transition-transform"
                        [class.rotate-180]="item.isOpen"
                    ></em>
                }
            </div>
        }
    </ng-template>
</ng-template>
